import React, { useEffect, useState } from 'react';

import QueryString from 'qs';

import { Autocomplete, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import UserName from '~/components/Common/UserName';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import { usePromo } from '~/hooks/usePromo';
import useQuery from '~/hooks/useQuery';

import { addDaysRaw } from '~/services/TimeService';

import Spinner from '../../Common/Spinner';
import DebugModal from '../../DebugModal/DebugModal';
import Table from '../../v2/common/Table/Table';
import PromoSearchLink from '../PromoPage/PromoSearchLink';
import usePromoTagSummaries, { PromoTagSummaries } from '../hooks/usePromoTagSummaries';

const getColumns = (renderExtended: boolean): Array<GridColDef<PromoTagSummaries>> => [
  {
    field: 'dept_tag',
    headerName: 'Dept Tag',
    flex: 1,
    renderCell: (params) => {
      return (
        <PromoSearchLink
          title={params.row.dept_tag}
          filter={{
            dept_tag: params.row.dept_tag,
            is_dev: true,
          }}
        />
      );
    },
  },
  {
    field: 'category_tag',
    headerName: 'Category Tag',
    flex: 1,
    renderCell: (params) => {
      return (
        <PromoSearchLink
          title={params.row.category_tag}
          filter={{
            dept_tag: params.row.dept_tag,
            category_tag: params.row.category_tag,
            is_dev: true,
          }}
        />
      );
    },
  },
  {
    field: 'sub_category_tag',
    headerName: 'Sub Category Tag',
    flex: 1,
    renderCell: (params) => {
      return (
        <PromoSearchLink
          title={params.row.sub_category_tag}
          filter={{
            dept_tag: params.row.dept_tag,
            category_tag: params.row.category_tag,
            sub_category_tag: params.row.sub_category_tag,
            is_dev: true,
          }}
        />
      );
    },
  },
  {
    field: 'unapprovedPromoCount',
    headerName: 'Unapproved Promos',
    flex: 2,
    renderCell: (params) => (
      <PromoSearchLink
        filter={{
          dept_tag: params.row.dept_tag,
          category_tag: params.row.category_tag,
          sub_category_tag: params.row.sub_category_tag,
          exclude_approved_promos: 'true',
          is_dev: true,
        }}
        title={`${params.row.unapprovedPromoCount} Unapproved`}
      />
    ),
  },
  {
    field: 'fixedCount',
    headerName: 'Fixed Count',
    flex: 1,
  },
  {
    field: 'percentageCount',
    headerName: 'Percentage Count',
    flex: 1,
  },
  {
    field: 'totalRemainingCount',
    headerName: 'Total Remaining Count',
    flex: 1,
  },
  {
    field: 'Users',
    headerName: 'Users',
    flex: renderExtended ? 3 : 1,
    renderCell: (params) => {
      if (!renderExtended) {
        return (
          <Stack direction="column" spacing={1}>
            <Typography variant="caption" title={`Distinct Created By: ${params.row.createdByUsers.length}`}>
              C: {params.row.createdByUsers.length}
            </Typography>
            <Typography variant="caption" title={`Distinct Updated By: ${params.row.updatedByUsers.length}`}>
              U: {params.row.updatedByUsers.length}
            </Typography>
            <Typography variant="caption" title={`Distinct Approved By: ${params.row.approvedByUsers.length}`}>
              A: {params.row.approvedByUsers.length}
            </Typography>
          </Stack>
        );
      } else {
        return (
          <Stack
            direction="column"
            spacing={2}
            title={`JSON: ${JSON.stringify(
              {
                createdByUsers: params.row.createdByUsers,
                updatedByUsers: params.row.updatedByUsers,
                approvedByUsers: params.row.approvedByUsers,
              },
              null,
              4,
            )}`}
          >
            <Typography variant="caption">Created By</Typography>
            {params.row.createdByUsers.map((u) => (
              <UserName key={`c-${u}`} userId={u} />
            ))}
            <Typography variant="caption">Updated By</Typography>
            {params.row.updatedByUsers.map((u) => (
              <UserName key={`u-${u}`} userId={u} />
            ))}
            <Typography variant="caption">Approved By</Typography>
            {params.row.approvedByUsers.map((u) => (
              <UserName key={`a-${u}`} userId={u} />
            ))}
          </Stack>
        );
      }
    },
  },
  {
    field: 'Debug',
    headerName: 'Debug',
    flex: 1,
    renderCell: (params) => <DebugModal title="Debug" type="generic" data={{ ...params.row }} />,
  },
];

function PromoTagSummary() {
  const query = useQuery();
  const { push } = useAppNavigation();
  const queryParams = {
    departmentTag: query.get('department_tag'),
    categoryTag: query.get('category_tag'),
    subCategoryTag: query.get('sub_category_tag'),
  };
  const { tenant } = useCurrentTenant();
  const [renderExtended, setRenderExtended] = useState<boolean>(false);
  const [createdAfterDate, setCreatedAfterDate] = useState<string | null>(null);
  const {
    isLoading: isPromoLoading,
    isPromoMetaLoading,
    promoTagArrays,
    initPromoData,
    setPromoTagSelection,
    promoTagSelections,
  } = usePromo({
    options: {
      autoSelectTag: true,
    },
    selectedDepartment: queryParams.departmentTag,
    selectedCategory: queryParams.categoryTag,
    selectedSubCategory: queryParams.subCategoryTag,
  });

  const handleOnClick = () => {
    const params = {
      brand: tenant.brand === 'newwhitelabel' || tenant.brand === 'yidu' ? 'luxuryescapes' : tenant.brand,
      dept_tag: promoTagSelections.selectedDepartment,
      category_tag: promoTagSelections.selectedCategory,
      sub_category_tag: promoTagSelections.selectedSubCategory,
      created_after: createdAfterDate,
    };
    refetch(params);

    const queryString = QueryString.stringify(params);
    push(`${window.location.pathname}?${queryString}`);
  };

  useEffect(() => {
    if (!promoTagArrays) {
      initPromoData(null);
    }
  }, []);

  const { tagSummaries, isLoading, error, refetch } = usePromoTagSummaries();

  if (isLoading || isPromoLoading || isPromoMetaLoading || !promoTagArrays) {
    return <Spinner />;
  }

  return (
    <Stack direction="column" spacing={1} mb={1}>
      <Typography variant="h6">Promo Tag Summaries</Typography>
      <Typography variant="caption" color="textSecondary">
        Promo Tag Summaries give an overview of which promo tags are in uses and by which users
      </Typography>
      <Stack direction="row" spacing={1}>
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            value={promoTagSelections.selectedDepartment}
            options={promoTagArrays.departmentTags}
            renderInput={(params) => <TextField {...params} variant="outlined" label="Department" />}
            onChange={(e: unknown, newValue: string | null) => {
              setPromoTagSelection(newValue || '', '', '');
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            value={promoTagSelections.selectedCategory}
            options={promoTagArrays.categoryTags}
            renderInput={(params) => <TextField {...params} variant="outlined" label="Category" />}
            onChange={(e: unknown, newValue: string | null) => {
              setPromoTagSelection(promoTagSelections.selectedDepartment, newValue || '', '');
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            value={promoTagSelections.selectedSubCategory}
            options={promoTagArrays.subCategoryTags}
            renderInput={(params) => <TextField {...params} variant="outlined" label="Sub Category" />}
            onChange={(e: unknown, newValue: string | null) => {
              setPromoTagSelection(
                promoTagSelections.selectedDepartment,
                promoTagSelections.selectedCategory,
                newValue || '',
              );
            }}
          />
        </Box>
        <Box>
          <DateTimeWidget
            label="Only include promos created after this date"
            value={createdAfterDate ?? addDaysRaw(-7).toISOString()}
            onChange={setCreatedAfterDate}
            disableFuture
          />
        </Box>
        <Button onClick={() => setRenderExtended(!renderExtended)}>Toggle Extended</Button>
        <Button variant="contained" onClick={handleOnClick}>
          Fetch Tag Summaries
        </Button>
      </Stack>
      {tagSummaries.length > 0 && (
        <Table
          getRowId={(row) => `${row.dept_tag}-${row.category_tag}-${row.sub_category_tag}`}
          columns={getColumns(renderExtended)}
          rows={tagSummaries}
          paginationMode="client"
          autoHeight
          dataGridProps={{
            getRowHeight: () => 'auto',
          }}
        />
      )}
      {error && (
        <Typography variant="caption" title={`Error: ${error?.message}`}>
          Error fetching promo tag summaries
        </Typography>
      )}
      {isLoading && (
        <Typography variant="caption" title={`Loading promo tag summaries...`}>
          Loading promo tag summaries...
        </Typography>
      )}
      {tagSummaries?.length === 0 && (
        <Typography variant="caption" title={`No promo tag summaries loaded`}>
          No promo tag summaries loaded
        </Typography>
      )}
    </Stack>
  );
}

export default PromoTagSummary;
